/**
 * Approval comments hook (v1.1-A8 / Phase B5) — read + post comments
 * on agent_tool_approvals via the A8 endpoints. Used by the approval
 * detail page comment thread.
 *
 * Same UUID-gate pattern as B4 (workflow canvas): only fire against
 * the API when the route id is a real UUID; otherwise the page
 * keeps its in-memory mock comment thread for demo approvals.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export interface ServerApprovalComment {
  id: string;
  agentToolApprovalId: string;
  authorId: string;
  body: string;
  isSystem: boolean;
  createdAt: string;
}

interface ListResponse {
  comments: ServerApprovalComment[];
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isApprovalIdReal(id: string): boolean {
  return UUID_RE.test(id);
}

export function useApprovalComments(approvalId: string) {
  return useQuery<ListResponse, Error>({
    queryKey: ['approval-comments', approvalId],
    enabled: isApprovalIdReal(approvalId),
    queryFn: () =>
      apiClient.get<ListResponse>(`/agent-approvals/${approvalId}/comments`),
  });
}

/**
 * fix-wave / agents-approvals-cluster #1106 — the hook no longer accepts
 * a body-supplied `authorId`. The backend `POST /:id/comments` route is
 * `.strict()` and rejects unknown keys, so sending `authorId` from the
 * client makes the request fail with 422 + `Unrecognized key: "authorId"`
 * and the comment is silently lost. The server derives `authorId` from
 * the authenticated principal (request.user.id) — there is no callsite
 * where the client gets to choose it.
 */
export function usePostApprovalComment(approvalId: string) {
  const qc = useQueryClient();
  return useMutation<
    ServerApprovalComment,
    Error,
    { body: string; isSystem?: boolean }
  >({
    mutationFn: (input) =>
      apiClient.post<ServerApprovalComment>(
        `/agent-approvals/${approvalId}/comments`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-comments', approvalId] });
    },
  });
}

export function useDeleteApprovalComment(approvalId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (commentId) =>
      apiClient.delete<void>(
        `/agent-approvals/${approvalId}/comments/${commentId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-comments', approvalId] });
    },
  });
}
